import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  AudioLines,
  CircleAlert,
  Clock3,
  Headphones,
  Mic,
  MicOff,
  PhoneCall,
  PhoneOff,
  Radio,
  RefreshCw,
  ShieldCheck,
  Volume2,
} from "lucide-react";
import { toast } from "sonner";
import { createSession } from "../../services/session";
import {
  closeRealtimeCall,
  createRealtimeCall,
  resumeRealtimeCall,
} from "../../services/realtime";
import { useGenerativeSessionSeed } from "../../context/GenerativeSessionContext";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { cn } from "../../lib/cn";

const STATUS = {
  idle: { label: "Pronto", tone: "default" },
  connecting: { label: "Conectando", tone: "warning" },
  active: { label: "Ao vivo", tone: "success" },
  reconnecting: { label: "Reconectando", tone: "warning" },
  error: { label: "Falha na conexão", tone: "danger" },
};

const unwrap = (response) => response?.data ?? response;

const withTimeout = (promise, timeoutMs, code) => {
  let timeout;
  const timeoutPromise = new Promise((_, reject) => {
    timeout = window.setTimeout(() => {
      const timeoutError = new Error(code);
      timeoutError.code = code;
      reject(timeoutError);
    }, timeoutMs);
  });

  return Promise.race([promise, timeoutPromise])
    .finally(() => window.clearTimeout(timeout));
};

const waitForIceGathering = (peer, timeoutMs = 5000) => {
  if (peer.iceGatheringState === "complete") return Promise.resolve();

  return new Promise((resolve) => {
    let timeout;
    const finish = () => {
      window.clearTimeout(timeout);
      peer.removeEventListener("icegatheringstatechange", handleStateChange);
      resolve();
    };
    const handleStateChange = () => {
      if (peer.iceGatheringState !== "complete") return;
      finish();
    };
    peer.addEventListener("icegatheringstatechange", handleStateChange);
    // Trickle ICE is not used by the current API. Keep the best local SDP
    // available instead of leaving the UI blocked forever on constrained networks.
    timeout = window.setTimeout(finish, timeoutMs);
  });
};

const formatDuration = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(remainingSeconds).padStart(2, "0")}`;
};

const errorMessage = (error) => {
  if (error?.code === "MIC_INSECURE") {
    return "O microfone só pode ser usado em uma conexão HTTPS segura.";
  }
  if (error?.code === "MIC_UNAVAILABLE") {
    return "Este navegador não oferece acesso ao microfone nesta página.";
  }
  if (error?.code === "MIC_PERMISSION_TIMEOUT") {
    return "O navegador não respondeu ao pedido de microfone. Verifique o ícone de permissão ao lado do endereço e tente novamente.";
  }
  if (error?.code === "SESSION_TIMEOUT") {
    return "A criação da sessão demorou demais. Tente novamente em alguns segundos.";
  }
  if (error?.code === "REALTIME_TIMEOUT") {
    return "A conexão de voz demorou demais para responder.";
  }
  if (error?.name === "NotAllowedError" || error?.name === "PermissionDeniedError") {
    return "O acesso ao microfone foi bloqueado. Autorize o microfone nas permissões do site e tente novamente.";
  }
  if (error?.name === "NotFoundError" || error?.name === "DevicesNotFoundError") {
    return "Nenhum microfone foi encontrado neste dispositivo.";
  }
  if (error?.name === "NotReadableError" || error?.name === "TrackStartError") {
    return "O microfone está sendo usado por outro aplicativo ou não pôde ser iniciado.";
  }
  if (error?.name === "SecurityError") {
    return "O navegador bloqueou o microfone por uma política de segurança.";
  }
  const status = error?.status;
  if (status === 402) return "O limite mensal de mensagens foi atingido.";
  if (status === 409) return "A chamada ainda está ativa no servidor. Tentando novamente…";
  if (status === 410) return "Esta chamada expirou e não pode mais ser retomada.";
  if (status === 422) return "Realtime não está habilitado para este assistente.";
  if (status === 502 || status === 504) return "O provedor de voz não respondeu.";
  return error?.data?.message || error?.message || "Não foi possível iniciar a chamada.";
};

export default function RealtimeVoiceTest({ assistantId, assistant }) {
  const { generativeSessionSeedId } = useGenerativeSessionSeed();
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const [muted, setMuted] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [callInfo, setCallInfo] = useState(null);
  const [hasCall, setHasCall] = useState(false);
  const [reconnectRequest, setReconnectRequest] = useState(null);

  const audioRef = useRef(null);
  const peerRef = useRef(null);
  const mediaRef = useRef(null);
  const dataChannelRef = useRef(null);
  const callRef = useRef(null);
  const sessionRef = useRef(null);
  const reconnectTimerRef = useRef(null);
  const startedAtRef = useRef(null);
  const mountedRef = useRef(true);

  const enabled = !!assistant?.realtimeEnabled;
  const busy = status === "connecting" || status === "reconnecting";
  const live = status === "active" || status === "reconnecting";

  const closePeer = useCallback(() => {
    const peer = peerRef.current;
    peerRef.current = null;
    dataChannelRef.current?.close();
    dataChannelRef.current = null;
    if (peer) {
      peer.ontrack = null;
      peer.onconnectionstatechange = null;
      peer.close();
    }
  }, []);

  const buildPeer = useCallback((stream, onConnectionLost) => {
    const peer = new RTCPeerConnection();
    stream.getAudioTracks().forEach((track) => peer.addTrack(track, stream));

    dataChannelRef.current = peer.createDataChannel("verboo-realtime-events");
    peer.ontrack = ({ streams }) => {
      if (!audioRef.current || !streams?.[0]) return;
      audioRef.current.srcObject = streams[0];
      audioRef.current.play().catch(() => {
        // Browsers may require one more user gesture; the audio control remains visible.
      });
    };
    peer.onconnectionstatechange = () => {
      if (peerRef.current !== peer) return;
      if (peer.connectionState === "failed") onConnectionLost(0);
      if (peer.connectionState === "disconnected") onConnectionLost(1);
    };
    peerRef.current = peer;
    return peer;
  }, []);

  const createOffer = useCallback(async (peer) => {
    const offer = await peer.createOffer();
    await peer.setLocalDescription(offer);
    await waitForIceGathering(peer);
    return {
      type: "offer",
      sdp: peer.localDescription?.sdp || offer.sdp,
    };
  }, []);

  const applyAnswer = useCallback(async (peer, response) => {
    const answer = unwrap(response);
    if (!answer?.answer?.sdp) throw new Error("O servidor não retornou uma resposta WebRTC.");
    await peer.setRemoteDescription({
      type: answer.answer.type || "answer",
      sdp: answer.answer.sdp,
    });
    return answer;
  }, []);

  const scheduleReconnect = useCallback((delaySeconds = 1, attempt = 0) => {
    if (!callRef.current || reconnectTimerRef.current) return;
    reconnectTimerRef.current = window.setTimeout(() => {
      reconnectTimerRef.current = null;
      setReconnectRequest({ id: Date.now(), attempt });
    }, delaySeconds * 1000);
  }, []);

  const reconnect = useCallback(async (attempt = 0) => {
    const currentCall = callRef.current;
    const currentSession = sessionRef.current;
    const stream = mediaRef.current;
    if (!currentCall || !currentSession || !stream || !mountedRef.current) return;

    setStatus("reconnecting");
    setError("");
    closePeer();

    try {
      const peer = buildPeer(stream, scheduleReconnect);
      const offer = await createOffer(peer);
      const response = await resumeRealtimeCall(
        currentSession.id,
        currentCall.call_id,
        currentCall.ticket,
        offer,
      );
      callRef.current = unwrap(response);
      setHasCall(true);
      const resumed = await applyAnswer(peer, response);
      if (!mountedRef.current) return;
      setCallInfo(resumed);
      setStatus("active");
    } catch (reconnectError) {
      if (!mountedRef.current) return;
      if (reconnectError?.status === 409 && attempt < 4) {
        scheduleReconnect(1.2 * (attempt + 1), attempt + 1);
        return;
      }
      setError(errorMessage(reconnectError));
      setStatus("error");
    }
  }, [applyAnswer, buildPeer, closePeer, createOffer, scheduleReconnect]);

  useEffect(() => {
    if (!reconnectRequest) return;
    const timer = window.setTimeout(() => reconnect(reconnectRequest.attempt), 0);
    return () => window.clearTimeout(timer);
  }, [reconnect, reconnectRequest]);

  const stop = useCallback(async ({ notify = true, resetSession = false } = {}) => {
    if (reconnectTimerRef.current) {
      window.clearTimeout(reconnectTimerRef.current);
      reconnectTimerRef.current = null;
    }

    const activeCall = callRef.current;
    const activeSession = sessionRef.current;
    callRef.current = null;
    closePeer();
    mediaRef.current?.getTracks().forEach((track) => track.stop());
    mediaRef.current = null;
    if (audioRef.current) audioRef.current.srcObject = null;

    if (activeCall && activeSession) {
      try {
        await closeRealtimeCall(activeSession.id, activeCall.call_id, activeCall.ticket);
      } catch (closeError) {
        if (notify) toast.error(errorMessage(closeError));
      }
    }

    if (!mountedRef.current) return;
    if (resetSession) sessionRef.current = null;
    startedAtRef.current = null;
    setElapsed(0);
    setMuted(false);
    setCallInfo(null);
    setHasCall(false);
    setError("");
    setStatus("idle");
  }, [closePeer]);

  const start = useCallback(async () => {
    if (!enabled || busy || live) return;
    setStatus("connecting");
    setError("");

    try {
      if (!window.isSecureContext) {
        const insecureError = new Error("MIC_INSECURE");
        insecureError.code = "MIC_INSECURE";
        throw insecureError;
      }
      if (!navigator.mediaDevices?.getUserMedia) {
        const unavailableError = new Error("MIC_UNAVAILABLE");
        unavailableError.code = "MIC_UNAVAILABLE";
        throw unavailableError;
      }

      // Ask for the microphone directly from the click gesture. Creating the
      // backend session first can delay or suppress the browser permission UI.
      const mediaRequest = navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
        },
      });
      const stream = await withTimeout(
        mediaRequest,
        20000,
        "MIC_PERMISSION_TIMEOUT",
      ).catch((mediaError) => {
        if (mediaError?.code === "MIC_PERMISSION_TIMEOUT") {
          mediaRequest
            .then((lateStream) => lateStream.getTracks().forEach((track) => track.stop()))
            .catch(() => {});
        }
        throw mediaError;
      });
      mediaRef.current = stream;

      let activeSession = sessionRef.current;
      if (!activeSession?.id) {
        const response = await withTimeout(
          createSession(
            assistantId,
            assistantId,
            true,
            generativeSessionSeedId,
          ),
          20000,
          "SESSION_TIMEOUT",
        );
        activeSession = response.data;
        sessionRef.current = activeSession;
      }

      const peer = buildPeer(stream, scheduleReconnect);
      const offer = await createOffer(peer);
      const response = await withTimeout(
        createRealtimeCall(activeSession.id, offer),
        35000,
        "REALTIME_TIMEOUT",
      );
      callRef.current = unwrap(response);
      setHasCall(true);
      const created = await applyAnswer(peer, response);

      startedAtRef.current = Date.now();
      if (!mountedRef.current) return;
      setCallInfo(created);
      setStatus("active");
    } catch (startError) {
      closePeer();
      if (!callRef.current) {
        mediaRef.current?.getTracks().forEach((track) => track.stop());
        mediaRef.current = null;
      }
      if (!mountedRef.current) return;
      setError(errorMessage(startError));
      setStatus("error");
    }
  }, [
    applyAnswer,
    assistantId,
    buildPeer,
    busy,
    closePeer,
    createOffer,
    enabled,
    generativeSessionSeedId,
    live,
    scheduleReconnect,
  ]);

  const toggleMute = useCallback(() => {
    const nextMuted = !muted;
    mediaRef.current?.getAudioTracks().forEach((track) => {
      track.enabled = !nextMuted;
    });
    setMuted(nextMuted);
  }, [muted]);

  useEffect(() => {
    if (!startedAtRef.current || !live) return undefined;
    const timer = window.setInterval(() => {
      setElapsed(Math.floor((Date.now() - startedAtRef.current) / 1000));
    }, 1000);
    return () => window.clearInterval(timer);
  }, [live]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (reconnectTimerRef.current) window.clearTimeout(reconnectTimerRef.current);
      const activeCall = callRef.current;
      const activeSession = sessionRef.current;
      callRef.current = null;
      closePeer();
      mediaRef.current?.getTracks().forEach((track) => track.stop());
      if (activeCall && activeSession) {
        closeRealtimeCall(activeSession.id, activeCall.call_id, activeCall.ticket).catch(() => {});
      }
    };
  }, [closePeer]);

  const provider = callInfo?.provider || assistant?.realtimeProvider || "OPENAI";
  const model = callInfo?.model || assistant?.realtimeModel || "gpt-realtime-2.1";
  const voice = assistant?.realtimeVoice || "marin";
  const statusInfo = STATUS[status] || STATUS.idle;

  const expiryLabel = callInfo?.expires_at
    ? new Date(callInfo.expires_at).toLocaleTimeString("pt-BR", {
      hour: "2-digit",
      minute: "2-digit",
    })
    : null;

  if (!enabled) {
    return (
      <div className="flex h-full min-h-[360px] flex-col items-center justify-center gap-5 rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface-subtle)] p-8 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl border border-[var(--dash-primary-border)] bg-[var(--dash-primary-soft)] text-[var(--dash-primary)]">
          <AudioLines size={24} />
        </div>
        <div className="max-w-sm">
          <h3 className="text-sm font-semibold text-[var(--dash-text)]">Voz ao vivo desativada</h3>
          <p className="mt-2 text-xs leading-5 text-[var(--dash-text-muted)]">
            Habilite o Realtime nas configurações administrativas do assistente e salve as alterações antes de iniciar um teste.
          </p>
        </div>
        <div className="flex items-center gap-2 text-[11px] text-[var(--dash-text-soft)]">
          <ShieldCheck size={13} />
          A credencial do provedor permanece protegida no servidor
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-2xl border border-[var(--dash-border)] bg-[var(--dash-surface-subtle)]">
      <audio ref={audioRef} autoPlay playsInline controls={false} />

      <div className="flex items-center justify-between gap-3 border-b border-[var(--dash-border)] px-4 py-3">
        <div className="flex min-w-0 items-center gap-3">
          <div className={cn(
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl border",
            live
              ? "border-emerald-500/30 bg-emerald-500/10 text-emerald-400"
              : "border-[var(--dash-primary-border)] bg-[var(--dash-primary-soft)] text-[var(--dash-primary)]",
          )}>
            {live ? <Radio size={16} /> : <Headphones size={16} />}
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="truncate text-xs font-semibold text-[var(--dash-text)]">Conversa por voz</span>
              <Badge variant={statusInfo.tone}>{statusInfo.label}</Badge>
            </div>
            <p className="mt-0.5 truncate text-[10px] text-[var(--dash-text-muted)]">
              {provider} · {model} · voz {voice}
            </p>
          </div>
        </div>
        {live && (
          <div className="flex items-center gap-1.5 text-[11px] font-medium tabular-nums text-[var(--dash-text-soft)]">
            <Clock3 size={12} />
            {formatDuration(elapsed)}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 p-6">
        <div className="relative flex h-40 w-40 items-center justify-center">
          {live && (
            <>
              <div className="absolute inset-0 animate-ping rounded-full border border-[var(--dash-primary-border)] opacity-30" />
              <div className="absolute inset-5 animate-pulse rounded-full bg-[var(--dash-primary-soft)]" />
            </>
          )}
          <div
            className={cn(
              "relative flex h-24 w-24 items-center justify-center rounded-full border transition-all duration-300",
              live
                ? "border-[var(--dash-primary)] bg-[var(--dash-primary-soft)] text-[var(--dash-primary)] shadow-[0_0_40px_rgba(173,52,254,0.25)]"
                : "border-[var(--dash-border-strong)] bg-[var(--dash-surface)] text-[var(--dash-text-soft)]",
            )}
          >
            {busy ? (
              <RefreshCw className="animate-spin" size={30} />
            ) : muted ? (
              <MicOff size={30} />
            ) : live ? (
              <VoiceBars />
            ) : (
              <PhoneCall size={30} />
            )}
          </div>
        </div>

        <div className="max-w-sm text-center">
          <h3 className="text-sm font-semibold text-[var(--dash-text)]">
            {status === "idle" && "Pronto para conversar"}
            {status === "connecting" && "Preparando microfone e conexão"}
            {status === "active" && (muted ? "Seu microfone está mudo" : "Pode falar naturalmente")}
            {status === "reconnecting" && "Recuperando a chamada"}
            {status === "error" && "A chamada não pôde continuar"}
          </h3>
          <p className="mt-2 text-xs leading-5 text-[var(--dash-text-muted)]">
            {error || (
              live
                ? "O áudio é transmitido diretamente por WebRTC. RAG, ferramentas e histórico continuam ativos no servidor."
                : "Ao iniciar, o navegador solicitará acesso ao microfone."
            )}
          </p>
          {expiryLabel && live && (
            <p className="mt-2 text-[10px] text-[var(--dash-text-soft)]">
              Sessão disponível até {expiryLabel}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center justify-center gap-2">
          {status === "idle" && (
            <Button variant="primary" leftIcon={PhoneCall} onClick={start}>
              Iniciar chamada
            </Button>
          )}
          {live && (
            <>
              <Button
                variant="outline"
                leftIcon={muted ? Mic : MicOff}
                onClick={toggleMute}
                disabled={busy}
              >
                {muted ? "Ativar microfone" : "Silenciar"}
              </Button>
              <Button variant="danger" leftIcon={PhoneOff} onClick={() => stop()}>
                Encerrar
              </Button>
            </>
          )}
          {status === "error" && (
            <>
              {hasCall ? (
                <Button variant="primary" leftIcon={RefreshCw} onClick={() => reconnect(0)}>
                  Tentar reconectar
                </Button>
              ) : (
                <Button variant="primary" leftIcon={PhoneCall} onClick={start}>
                  Tentar novamente
                </Button>
              )}
              <Button variant="outline" leftIcon={PhoneOff} onClick={() => stop()}>
                Encerrar
              </Button>
            </>
          )}
        </div>

        {status === "error" && (
          <div className="flex items-center gap-2 rounded-lg border border-red-500/20 bg-red-500/5 px-3 py-2 text-[11px] text-red-400">
            <CircleAlert size={13} />
            Verifique se o serviço Realtime e o assistente estão habilitados.
          </div>
        )}
      </div>

      <div className="flex items-center justify-between gap-3 border-t border-[var(--dash-border)] px-4 py-3 text-[10px] text-[var(--dash-text-muted)]">
        <span className="flex items-center gap-1.5">
          <ShieldCheck size={12} />
          Credenciais protegidas
        </span>
        <span className="flex items-center gap-1.5">
          <Volume2 size={12} />
          WebRTC
        </span>
      </div>
    </div>
  );
}

function VoiceBars() {
  return (
    <div className="flex h-9 items-center gap-1" aria-label="Áudio ativo">
      {[16, 28, 36, 24, 32, 18].map((height, index) => (
        <span
          key={height + index}
          className="w-1 animate-pulse rounded-full bg-current"
          style={{
            height,
            animationDelay: `${index * 90}ms`,
            animationDuration: `${700 + index * 80}ms`,
          }}
        />
      ))}
    </div>
  );
}
